import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlagIcon from '@mui/icons-material/Flag';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER, DEFAULT_DARK_GRADIENT } from '../../constants/theme';
import { useLanguageContext } from '../../contexts/LanguageContext';
import useFreaksT from '../TheFreaks/useFreaksT';
import FreaksHero from '../TheFreaks/FreaksHero';
import FreaksDailyPrize from '../TheFreaks/FreaksDailyPrize';
import FreaksMissions from '../TheFreaks/FreaksMissions';
import FreaksTraining from '../TheFreaks/FreaksTraining';
import FreaksGearList from '../TheFreaks/FreaksGearList';
import FreaksBase from '../TheFreaks/FreaksBase';
import FreaksAvatar from '../TheFreaks/FreaksAvatar';
import FreaksBotBadge from '../TheFreaks/FreaksBotBadge';
import { contextIcon } from '../TheFreaks/contextIcon';
import { actionSx, FREAKS_GOLD, FREAKS_RED, freaksSx as s } from '../TheFreaks/freaksStyles';
import theFreaksGuideTranslations from './theFreaksGuideTranslations';
import { THE_FREAKS_GUIDE_PATH } from './TheFreaksGuideView';
import { DEMO_STEPS, demoStates } from './theFreaksDemoScript';

// how long the pointer rests on a button before "pressing" it, and how long the result stays up
export const POINT_MS = 1800;
export const RESULT_MS = 2700;
export const CAPTION_MS = 3500;

const TABS = [
  { key: 'missions', Icon: FlagIcon },
  { key: 'train', Icon: FitnessCenterIcon },
  { key: 'gear', Icon: HomeRepairServiceIcon },
  { key: 'base', Icon: StorefrontIcon },
  { key: 'fight', Icon: SportsMmaIcon },
  { key: 'top', Icon: EmojiEventsIcon },
];

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(245,197,66,0.9); }
  70% { box-shadow: 0 0 0 14px rgba(245,197,66,0); }
  100% { box-shadow: 0 0 0 0 rgba(245,197,66,0); }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;

const fill = (template, params) => template.replace(/%\{(\w+)\}/g, (_, key) => params[key] ?? '');

// Same look as the game's tab bar, but only shows where the demo is (not clickable).
const DemoTabs = ({ tab, t }) => {
  const rowRef = useRef(null);
  useEffect(() => {
    const active = rowRef.current?.querySelector('[aria-selected="true"]');
    active?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [tab]);
  return (
    <Box ref={rowRef} role="tablist" sx={{ display: 'flex', gap: 1, overflowX: 'hidden', mb: 1.5, pb: 0.5 }}>
      {TABS.map(({ key, Icon }) => (
        <Box
          key={key}
          role="tab"
          aria-selected={tab === key}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, minHeight: 40, borderRadius: 5, px: 1.75,
            fontSize: 14, whiteSpace: 'nowrap', transition: 'background-color .3s',
            fontWeight: tab === key ? 700 : 500,
            color: tab === key ? 'white' : 'rgba(255,255,255,0.8)',
            backgroundColor: tab === key ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.08)',
          }}
        >
          <Icon sx={{ fontSize: 20 }}/>
          {t(`theFreaks.tabs.${key}`)}
        </Box>
      ))}
    </Box>
  );
};

// The game's Fight tab with one scripted opponent (the real one loads targets from the API).
const DemoFight = ({ state, t }) => (
  <Box data-testid="freaks-fight">
    <Typography sx={s.intro}>{t('theFreaks.fight.intro')}</Typography>
    <Typography sx={{ ...s.bold, mb: 1, color: 'white' }}>
      🥊 {t('theFreaks.fight.record', { won: state.record.challengesWon, lost: state.record.challengesLost })}
    </Typography>
    {state.opponent ? (
      <Box sx={{ ...s.card, ...s.between }}>
        <FreaksAvatar username={state.opponent.username}/>
        <Box sx={s.grow}>
          <Box sx={s.row}>
            <Typography noWrap sx={{ ...s.bold, flexShrink: 1 }}>{state.opponent.username}</Typography>
            <FreaksBotBadge/>
          </Box>
          <Box sx={{ ...s.row, gap: 0.5 }}>
            {contextIcon(state.opponent.context, { fontSize: 14, color: '#888' })}
            <Typography sx={{ ...s.small, ...s.muted }}>
              {t('theFreaks.level', { level: state.opponent.level })} · {state.opponent.respect} {t('theFreaks.respect').toLowerCase()}
            </Typography>
          </Box>
        </Box>
        <Button data-testid="freaks-challenge-demo" startIcon={<SportsMmaIcon/>} tabIndex={-1} sx={actionSx(FREAKS_RED)}>
          {t('theFreaks.fight.challenge')}
        </Button>
      </Box>
    ) : null}
    <Typography sx={{ ...s.small, ...s.muted, textAlign: 'center' }}>
      ⚡ {t('theFreaks.fight.cost', { energy: state.challengeEnergy })}
    </Typography>
  </Box>
);

/**
 * Public, auto-playing walkthrough of The Freaks for social media (screen-record the phone frame).
 * Runs the real game components on scripted data — no login and no API calls. Reached from the guide.
 */
const TheFreaksDemoView = () => {
  const navigate = useNavigate();
  const { language, handleChangeLanguage } = useLanguageContext();
  const { t } = useFreaksT();
  const g = theFreaksGuideTranslations[language] || theFreaksGuideTranslations.en;
  const d = g.demo;

  const states = useMemo(() => demoStates(language), [language]);
  const [index, setIndex] = useState(0);
  const [pressed, setPressed] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [now, setNow] = useState(Date.now());
  const startedAt = useRef(Date.now());
  const screenRef = useRef(null);

  const step = DEMO_STEPS[index];
  const scripted = !!step.play;
  const last = index === DEMO_STEPS.length - 1;
  const shown = pressed ? states[index + 1] : states[index];
  // a real countdown to the next energy point, so the hero looks alive
  const state = { ...shown, nextEnergyAt: new Date(startedAt.current + 165000).toISOString() };
  const toast = pressed && scripted ? step.play(states[index], t).toast : null;

  const goTo = (next) => {
    setIndex(Math.max(0, Math.min(DEMO_STEPS.length - 1, next)));
    setPressed(false);
  };

  // autoplay: point at the button, press it, show the result, move on; stop on the last caption
  useEffect(() => {
    if (!playing || last) return undefined;
    const timer = scripted && !pressed
      ? setTimeout(() => setPressed(true), POINT_MS)
      : setTimeout(() => {
        setIndex((i) => i + 1);
        setPressed(false);
      }, scripted ? RESULT_MS : CAPTION_MS);
    return () => clearTimeout(timer);
  }, [playing, index, pressed, scripted, last]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // keep the button being pressed in view inside the phone screen
  useEffect(() => {
    const screenEl = screenRef.current;
    if (!screenEl) return;
    const target = step.target && screenEl.querySelector(`[data-testid="${step.target}"]`);
    if (target?.scrollIntoView) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    else if (!step.target) screenEl.scrollTo?.({ top: 0, behavior: 'smooth' });
  }, [index, step.target]);

  const caption = d.captions[step.key];
  const highlight = step.target && !pressed ? {
    [`& [data-testid="${step.target}"]`]: { animation: `${pulse} 1.1s ease-out infinite`, outline: `2px solid ${FREAKS_GOLD}`, outlineOffset: 2 },
  } : {};
  const noop = () => {};

  const control = (label, Icon, onClick, testId) => (
    <IconButton aria-label={label} title={label} onClick={onClick} data-testid={testId} sx={{ color: 'white', width: 44, height: 44 }}>
      <Icon/>
    </IconButton>
  );

  return (
    <Box
      data-testid="freaks-demo"
      sx={{
        minHeight: '100dvh', background: DEFAULT_DARK_GRADIENT, color: 'white',
        display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box', pt: { xs: 0, sm: 2 },
      }}
    >
      {/* the phone: crop to this frame when recording */}
      <Box
        sx={{
          position: 'relative', width: '100%', maxWidth: { sm: 420 }, height: { xs: 'calc(100dvh - 60px)', sm: 'min(860px, calc(100dvh - 92px))' },
          borderRadius: { xs: 0, sm: 6 }, border: { xs: 'none', sm: '2px solid rgba(255,255,255,0.12)' }, overflow: 'hidden',
          background: 'linear-gradient(180deg, #0d1b2a 0%, #091420 100%)', display: 'flex', flexDirection: 'column',
          boxShadow: { sm: '0 20px 60px rgba(0,0,0,0.6)' },
        }}
      >
        <Box
          key={`${step.key}-caption`}
          data-testid="freaks-demo-caption"
          aria-live="polite"
          sx={{ px: 2, py: 1.5, background: `${DEFAULT_THEME_COLOR}26`, borderBottom: `1px solid ${DEFAULT_THEME_COLOR}55`, animation: `${fadeUp} .45s ease-out` }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: DEFAULT_THEME_COLOR }}>
            {d.badge.toUpperCase()} · {index + 1}/{DEMO_STEPS.length}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.25 }}>{caption.title}</Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>{caption.text}</Typography>
        </Box>

        <Box ref={screenRef} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 1.5, pt: 1.5, pb: 10, pointerEvents: 'none', ...highlight }}>
          <FreaksHero state={state} now={now} busy={false} onPickContext={noop} onRefill={noop} onRecover={noop}/>
          <FreaksDailyPrize daily={state.daily} busy={false} onClaim={noop}/>
          <DemoTabs tab={step.tab} t={t}/>
          {step.tab === 'missions'
            ? <FreaksMissions missions={state.missions} energy={state.energy} disabled={false} maxRepeat={state.maxMissionRepeat} onPlay={noop}/>
            : null}
          {step.tab === 'train' ? <FreaksTraining state={state} energy={state.energy} disabled={false} onTrain={noop}/> : null}
          {step.tab === 'gear' ? <FreaksGearList gear={state.gear} cash={state.cash} disabled={false} onBuy={noop}/> : null}
          {step.tab === 'base' ? <FreaksBase base={state.base} cash={state.cash} disabled={false} onCollect={noop} onUpgrade={noop}/> : null}
          {step.tab === 'fight' ? <DemoFight state={state} t={t}/> : null}
        </Box>

        {toast ? (
          <Alert
            key={`${step.key}-toast`}
            data-testid="freaks-demo-toast"
            severity="success"
            variant="filled"
            sx={{ position: 'absolute', left: 12, right: 12, bottom: 16, fontWeight: 700, animation: `${fadeUp} .3s ease-out` }}
          >
            {toast}
          </Alert>
        ) : null}

        {last ? (
          <Box
            data-testid="freaks-demo-outro"
            sx={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', px: 3, gap: 2, background: 'rgba(9,20,32,0.92)', animation: `${fadeUp} .5s ease-out`,
            }}
          >
            <Box sx={{ fontSize: '4rem' }}>🏆</Box>
            <Typography sx={{ fontWeight: 900, fontSize: 26, letterSpacing: 2 }}>{t('theFreaks.title').toUpperCase()}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{caption.title}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>{caption.text}</Typography>
            <Button
              size="large"
              variant="contained"
              onClick={() => navigate('/register')}
              sx={{ background: DEFAULT_GRADIENT, color: 'white', px: 4, minHeight: 48, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
            >
              {g.register}
            </Button>
            <Typography sx={{ color: DEFAULT_THEME_COLOR, fontWeight: 700 }}>freak-land.com</Typography>
          </Box>
        ) : null}
      </Box>

      {/* controls stay outside the phone so they're not in the recording */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5, minHeight: 60, px: 1 }}>
        {control(d.backToGuide, ArrowBackIcon, () => navigate(THE_FREAKS_GUIDE_PATH), 'freaks-demo-back')}
        {control(d.previous, SkipPreviousIcon, () => goTo(index - 1), 'freaks-demo-prev')}
        {last
          ? control(d.restart, ReplayIcon, () => { goTo(0); setPlaying(true); }, 'freaks-demo-restart')
          : control(playing ? d.pause : d.play, playing ? PauseIcon : PlayArrowIcon, () => setPlaying(!playing), 'freaks-demo-toggle')}
        {control(d.next, SkipNextIcon, () => goTo(index + 1), 'freaks-demo-next')}
        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', mx: 1 }}>
          {fill(d.step, { step: index + 1, total: DEMO_STEPS.length })}
        </Typography>
        {['en', 'pl'].map((lang) => (
          <Button
            key={lang}
            size="small"
            onClick={() => handleChangeLanguage(lang)}
            sx={{ minWidth: 44, minHeight: 36, color: language === lang ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.6)', fontWeight: language === lang ? 800 : 400 }}
          >
            {lang.toUpperCase()}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default TheFreaksDemoView;
